import React, { createContext, useCallback, useContext, useRef, useState } from "react";
import { detailIdFromPath, initialPageFromUrl, usePageUrlSync } from "../hooks/usePageUrlSync";
import type { Product } from "../types";

export interface NavContextValue {
  page: string;
  setPage: React.Dispatch<React.SetStateAction<string>>;
  selProd: Product | null;
  setSelProd: React.Dispatch<React.SetStateAction<Product | null>>;
  /**
   * Minimal navigation helper: updates `page` (and optional selected
   * product) and lets {@link usePageUrlSync} push the new path to the URL.
   *
   * NOTE: the monolith's `NutriMartShell` wraps this with a richer `nav`
   * that also closes drawers, resets the checkout wizard and shows a
   * loading state. The shell registers that implementation via
   * {@link installNav} so every consumer of `useNav().nav` gets the
   * enriched behaviour without prop drilling.
   */
   nav: (page: string, prod?: Product | null) => void;
  /**
   * Lets a parent (typically `NutriMartShell`) override the default
   * `nav` implementation. The override is stored in a ref so swapping
   * it does NOT re-render context consumers; `nav` keeps its stable
   * identity and dispatches to the latest registered function.
   */
  installNav: (fn: (page: string, prod?: Product | null) => void) => void;
  /**
   * Product id extracted from a `/detail/<id>` deep link, awaiting
   * resolution against the catalog. Cleared after the shell sets `selProd`.
   */
  pendingDetailId: string | null;
  setPendingDetailId: (id: string | null) => void;
}

const NavContext = createContext<NavContextValue | null>(null);

export function NavProvider({
  children,
  initialNav,
  syncUrl = true,
}: {
  children: React.ReactNode;
  /**
   * Pre-installed nav implementation, available from the very first
   * render. Mostly useful for tests that want to assert a spy was
   * called without having to wait for `installNav` to fire from an
   * effect.
   */
  initialNav?: (page: string, prod?: Product | null) => void;
  /**
   * When true (default) NavProvider keeps the `page` state in sync with
   * the URL via {@link usePageUrlSync}. Tests that mount NavProvider
   * outside a React Router context should pass `syncUrl={false}` to
   * avoid the `useLocation() may be used only in the context of a
   * <Router>` invariant.
   */
  syncUrl?: boolean;
}) {
  const [page, setPage] = useState<string>(initialPageFromUrl);
  const [selProd, setSelProd] = useState<Product | null>(null);
  const [pendingDetailId, setPendingDetailId] = useState<string | null>(() => {
    if (typeof window === "undefined") return null;
    return detailIdFromPath(window.location.pathname);
  });

  // Render the URL sync as a child so the hook is only invoked when a
  // Router is present (tests can opt out via syncUrl={false}).
  // eslint-disable-next-line react-hooks/rules-of-hooks

  const defaultNav = useCallback((pg: string, prod: Product | null = null) => {
    setPage(pg);
    setSelProd(prod);
  }, []);

  const navRef = useRef<(page: string, prod?: Product | null) => void>(initialNav ?? defaultNav);
  const installNav = useCallback((fn: (page: string, prod?: Product | null) => void) => {
    navRef.current = fn;
  }, []);
  const nav = useCallback((pg: string, prod: Product | null = null) => {
    navRef.current(pg, prod);
  }, []);

  const value: NavContextValue = {
    page, setPage, selProd, setSelProd, nav, installNav,
    pendingDetailId, setPendingDetailId,
  };
  return (
    <NavContext.Provider value={value}>
      {syncUrl ? (
        <PageUrlSync
          page={page}
          setPage={setPage}
          detailId={(selProd as any)?.slug ?? selProd?.id ?? null}
          onDetailIdFromUrl={setPendingDetailId}
        />
      ) : null}
      {children}
    </NavContext.Provider>
  );
}

function PageUrlSync({
  page,
  setPage,
  detailId,
  onDetailIdFromUrl,
}: {
  page: string;
  setPage: (p: string) => void;
  detailId: string | number | null;
  onDetailIdFromUrl: (id: string | null) => void;
}) {
  usePageUrlSync(page, setPage, { detailId, onDetailIdFromUrl });
  return null;
}

export function useNav(): NavContextValue {
  const v = useContext(NavContext);
  if (!v) throw new Error("useNav must be used inside <NavProvider>");
  return v;
}